import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { LookupService } from '@services/lookup.service';
import { TreeNode } from 'primeng/api';
import { TreeViewService } from '@services/treeview.service';
import { IndustryClassificationsCriteria } from './filter-industry-classifications.model';
import { NgModel } from '@angular/forms';
import { IndustryType } from '@models/industryType.model';
import { List } from 'linqts-es-module';
import { IndustryCode } from '@models/industryCode.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FilterUtilService } from '../shared/filter-util.service';
import { AppState } from '../../../reducers';
import { select, Store } from '@ngrx/store';
import { selectSearchStrategyDatabaseIds } from '../../analysis/search-strategy/state/study-search-strategy.selectors';
import { selectIndustryClassificationFilterPassIfNAActivationFromAppSetting } from 'src/app/app.selectors';
import { Study } from '@models/study.model';
import { selectStudyData } from '../../study.selectors';

@Component({
  selector: 'app-filter-industry-classifications',
  templateUrl: './filter-industry-classifications.component.html',
  styleUrls: ['./filter-industry-classifications.component.css'],
})
export class FilterIndustryClassificationsComponent implements OnInit, OnDestroy {
  @Input() filter: any;
  @Input() submitClicked: boolean;
  @Input() readOnly = false;

  industrycode_multi: TreeNode[];
  industryTypes: IndustryType[] = [];
  filteredSelectedIndustryCodeNodes: TreeNode[];

  industryCodesList: List<IndustryCode> = new List<IndustryCode>();

  selectedIndustryCodeNodes: TreeNode[];
  lstSortTree: List<TreeNode> = new List<TreeNode>();
  filteredText = '';
  public selectedDatabaseIds: number[];
  public IndustryClassificationPassIfNA: string;
  study: Study;

  private destroy$ = new Subject();

  constructor(
    private lookupService: LookupService,
    private treeviewService: TreeViewService,
    private filterUtilService: FilterUtilService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectSearchStrategyDatabaseIds))
      .subscribe((ids) => {
        this.selectedDatabaseIds = ids;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectIndustryClassificationFilterPassIfNAActivationFromAppSetting))
      .subscribe((passifNaActivationFrom) => {
        this.IndustryClassificationPassIfNA = passifNaActivationFrom;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectStudyData))
      .subscribe((study) => {
        this.study = study;
      });
    this.loadInitialData();
  }

  async initializeForm(): Promise<void> {
    const filter = <IndustryClassificationsCriteria>this.filter;
    const passifNaDate = new Date(this.IndustryClassificationPassIfNA);
    const createdOnDate = new Date(this.study.createdOn);

    if (filter.isPassIfNa === null) {
      filter.isPassIfNa = passifNaDate < createdOnDate ? true : false;
    }

    if (filter.industryCodeSelectionId) {
      this.industryTypeChange(true);
    }
  }

  ShowValidationMessage(item: NgModel): boolean {
    return (item.errors || false) && (this.submitClicked || !item.pristine || item.touched);
  }

  loadInitialData() {
    this.filterUtilService.spinnerRS.next(true);

    this.lookupService
      .GetIndustryTypeByDatabaseId$(this.selectedDatabaseIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe((_industryTypes) => {
        _industryTypes.forEach((value) => {
          this.industryTypes.push(value.industryType);
        });
        this.initializeForm();
        this.filterUtilService.spinnerRS.next(false);
      });
  }

  public sortSelection(selection: TreeNode[]): void {
    return this.filterUtilService.sortSelection(selection);
  }

  async industryTypeChange(isUpdate = false) {
    this.industrycode_multi = [];
    this.industryCodesList = new List<IndustryCode>();
    this.filterUtilService.spinnerRS.next(true);

    const filter = <IndustryClassificationsCriteria>this.filter;
    if (filter.industryTypeId) {
      filter.industryTypeText = this.industryTypes.find((a) => a.industryTypeId === filter.industryTypeId).industryTypeName;

      if (!isUpdate) {
        filter.industryCodeSelectionId = [];
        filter.industryCodeSelectionLabel = [];
        this.selectedIndustryCodeNodes = [];
        this.filteredSelectedIndustryCodeNodes = [];
      }

      this.industryCodesList.AddRange(await this.lookupService.GetAllActiveIndustryCode(filter.industryTypeId));

      this.industrycode_multi = this.getIndustryCodes(null); // Start with ParentId = null

      if (isUpdate) {
        this.selectedIndustryCodeNodes = [];
        this.filteredSelectedIndustryCodeNodes = [];

        this.treeviewService.checkNodeIndustryClassification(this.industrycode_multi, filter.industryCodeSelectionId, this.selectedIndustryCodeNodes);

        if (this.selectedIndustryCodeNodes && this.selectedIndustryCodeNodes.length > 0) {
          this.selectedIndustryCodeNodes.forEach((val) => {
            this.filteredSelectedIndustryCodeNodes.push({ ...val });
          });
        }

        this.selectedIndustryCodeNodes.forEach((val) => {
          if (!filter.industryCodeSelectionLabel || filter.industryCodeSelectionLabel.filter((a) => a.data === val.data).length === 0) {
            filter.industryCodeSelectionLabel.push({ label: val.label, data: val.data });
          }

          if (!filter.industryCodeSelectionId.includes(val.data)) {
            filter.industryCodeSelectionId.push(val.data);
          }
        });

        this.sortSelection(filter.industryCodeSelectionLabel);

        this.arrangeIndustryCodeData(filter);
      }
    }

    this.filterUtilService.spinnerRS.next(false);
  }

  // Created Recursion method to bind N number of parent child relation.
  getIndustryCodes(industryCodeId?: number): TreeNode[] {
    // value to be return from this method.
    const industryCodeList: List<TreeNode> = new List<TreeNode>();

    // loop through indutry code to form list of parent - child
    this.industryCodesList
      .Where((ind) => ind.parentIndustryCodeId === industryCodeId)
      .ForEach((val) => {
        // Bind Parent data
        industryCodeList.Add({
          label: val.industryCodeValues + '. ' + val.industryName,
          data: val.industryCodeId,
          leaf: this.industryCodesList.Where((ind) => ind.parentIndustryCodeId === industryCodeId).Count() > 0 ? false : true,
          selectable: !this.readOnly,
        });

        // Get Parent Data
        const industryCode = industryCodeList.Where((a) => a.data === val.industryCodeId).FirstOrDefault();

        // Get Child Data
        const getChildIndustryCodes = this.getIndustryCodes(val.industryCodeId);

        // Bind Child Data
        if (getChildIndustryCodes.length > 0) {
          industryCode.children = getChildIndustryCodes;
          industryCode.leaf = false;
        } else {
          industryCode.leaf = true;
        }
      });
    return industryCodeList.ToArray();
  }

  getFilteredIndustryCode() {
    const filter = <IndustryClassificationsCriteria>this.filter;

    if (this.filteredText.length > 0) {
      const parentData: TreeNode[] = this.filterIndustryCodeTree(this.getIndustryCodes(null), this.filteredText);
      this.industrycode_multi = parentData;
    } else {
      this.industrycode_multi = this.getIndustryCodes(null);
    }

    this.selectedIndustryCodeNodes = [];
    this.treeviewService.checkNodeIndustryClassification(this.industrycode_multi, filter.industryCodeSelectionId, this.selectedIndustryCodeNodes);
  }

  industryNodeSelect() {
    const filter = <IndustryClassificationsCriteria>this.filter;

    if (this.selectedIndustryCodeNodes && this.selectedIndustryCodeNodes.length > 0) {
      this.selectedIndustryCodeNodes.forEach((val) => {
        const index = this.filteredSelectedIndustryCodeNodes.findIndex((a) => a.data === val.data);
        if (index === -1) {
          this.filteredSelectedIndustryCodeNodes.push({ ...val });
        }

        if (!filter.industryCodeSelectionId.includes(val.data)) {
          filter.industryCodeSelectionId.push(val.data);
        }

        if (!filter.industryCodeSelectionLabel || filter.industryCodeSelectionLabel.filter((a) => a.data === val.data).length === 0) {
          filter.industryCodeSelectionLabel.push({ label: val.label, data: val.data });
        }
      });
    }

    this.selectedIndustryCodeNodes = [];
    this.treeviewService.checkNodeIndustryClassification(this.industrycode_multi, filter.industryCodeSelectionId, this.selectedIndustryCodeNodes);

    this.sortSelection(filter.industryCodeSelectionLabel);

    this.arrangeIndustryCodeData(filter);
    this.filteredSelectedIndustryCodeNodes.forEach((node) => {
      node.leaf = false;
    });
  }

  industryNodeUnselect(event: any) {
    const filter = <IndustryClassificationsCriteria>this.filter;
    this.removeIndustryCodeNodes(event.node, filter);
    this.selectedIndustryCodeNodes = [];
    this.treeviewService.checkNodeIndustryClassification(this.industrycode_multi, filter.industryCodeSelectionId, this.selectedIndustryCodeNodes);
  }

  private arrangeIndustryCodeData(filter: IndustryClassificationsCriteria) {
    this.lstSortTree = new List<TreeNode>();
    this.lstSortTree.AddRange(this.filteredSelectedIndustryCodeNodes);
    this.lstSortTree.OrderBy((a) => a.label);
    this.filteredSelectedIndustryCodeNodes = [];
    this.filteredSelectedIndustryCodeNodes = this.lstSortTree.ToArray();

    this.lstSortTree = new List<TreeNode>();
    this.lstSortTree.AddRange(filter.industryCodeSelectionLabel);
    this.lstSortTree.OrderBy((a) => a.label);
    filter.industryCodeSelectionLabel = [];
    filter.industryCodeSelectionLabel = this.lstSortTree.ToArray();
  }

  private removeIndustryCodeNodesRecursively(node: TreeNode, filter: IndustryClassificationsCriteria): IndustryClassificationsCriteria {
    this.removeIndustryCodeNodes(node, filter);

    if (node.children && node.children.length > 0) {
      node.children.forEach((elem) => {
        this.removeIndustryCodeNodesRecursively(elem, filter);
      });
    }

    return filter;
  }

  private removeIndustryCodeNodes(node: TreeNode, filter: IndustryClassificationsCriteria): IndustryClassificationsCriteria {
    let index = -1;

    index = filter.industryCodeSelectionId.indexOf(node.data);
    if (index !== -1) {
      filter.industryCodeSelectionId.splice(index, 1);
    }

    index = filter.industryCodeSelectionLabel.findIndex((a) => a.data === node.data);
    if (index !== -1) {
      filter.industryCodeSelectionLabel.splice(index, 1);
    }

    index = this.filteredSelectedIndustryCodeNodes.findIndex((a) => a.data === node.data);
    if (index !== -1) {
      this.filteredSelectedIndustryCodeNodes.splice(index, 1);
    }

    index = this.selectedIndustryCodeNodes.findIndex((a) => a.data === node.data);
    if (index !== -1) {
      this.selectedIndustryCodeNodes.splice(index, 1);
    }

    return filter;
  }

  private filterIndustryCodeTree(elements: TreeNode[], matchingTitle: string): TreeNode[] {
    elements = elements.filter((el) => {
      if (this.isMatch(el.label, matchingTitle)) {
        return el;
      } else if (el.children && el.children.length > 0) {
        el.children = this.filterIndustryCodeTree(el.children, matchingTitle);
        if (el.children && el.children.length > 0) {
          return el;
        }
      }
      return false;
    });
    return elements;
  }

  private isMatch(label: string, matchingTitle: string): boolean {
    return label.toLowerCase().indexOf(matchingTitle.toLowerCase()) > -1;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
